const ADMIN_LOGIN_DEVICES = {
	// Both are the iOS app. The id lives in the Keychain, so unlike the PWA's it survives a reinstall
	// — but not a change of bundle id or team, which would scope the Keychain item somewhere else.
	david: "6f03eb7a29db60802daae22e5e1430fe",
	eben: "267f730909225571eb1afd4591ebbc41",
} as const;

export type AdminLoginName = keyof typeof ADMIN_LOGIN_DEVICES;

export function resolveAdminLogin(value: string): { username: AdminLoginName; deviceId: string } | null {
	const username = value.trim().toLowerCase() as AdminLoginName;
	const deviceId = ADMIN_LOGIN_DEVICES[username];
	return deviceId ? { username, deviceId } : null;
}

export function configuredAdminLoginNames(): AdminLoginName[] {
	return Object.keys(ADMIN_LOGIN_DEVICES) as AdminLoginName[];
}

/**
 * Whose device this is.
 *
 * Matched on a prefix, because the console only ever sees the first few characters of a device
 * id. Anything unrecognised has no owner rather than a guessed one.
 */
export function adminLoginForDevice(deviceId: string | null | undefined): AdminLoginName | null {
	const value = (deviceId ?? "").trim().toLowerCase();
	if (value.length < 3) {
		return null;
	}
	const entries = Object.entries(ADMIN_LOGIN_DEVICES) as Array<[AdminLoginName, string]>;
	return entries.find(([, id]) => id.startsWith(value) || value.startsWith(id))?.[0] ?? null;
}

export function isCurrentAdminLogin(value: string, authenticatedUsername: string): boolean {
	return resolveAdminLogin(value)?.username === authenticatedUsername.trim().toLowerCase();
}

/**
 * Who watches whose journey. Directional: the follower sees the subject, never the other way.
 *
 * Keyed on people rather than phones, and that is the whole point. This pairing used to be a row
 * of two device ids, written once by migration 0007 — so the evening both of them moved from the
 * Home Screen PWA to the app, follow and Pickup Mode stopped working and nothing said so. People
 * change phones; who drives whom does not.
 */
const FOLLOWS: Partial<Record<AdminLoginName, AdminLoginName>> = {
	david: "eben",
};

/** What a follower is told the subject is called. */
const DISPLAY_NAMES: Record<AdminLoginName, string> = {
	david: "David",
	eben: "Eben",
};

export function displayName(owner: AdminLoginName): string {
	return DISPLAY_NAMES[owner];
}

/** The phone someone is using now. */
export function deviceIdFor(owner: AdminLoginName): string {
	return ADMIN_LOGIN_DEVICES[owner];
}

/**
 * The phone watching this one, resolved through their owners rather than a stored pair.
 *
 * Null when nobody follows this device, which is also the answer for a retired phone: its owner is
 * still known, but the journey worth watching is the one on the phone they carry now.
 */
export function followerFor(subjectDeviceId: string): { deviceId: string; subjectName: string } | null {
	const subject = deviceOwner(subjectDeviceId);
	if (!subject || deviceIdFor(subject) !== subjectDeviceId) {
		return null;
	}
	const entries = Object.entries(FOLLOWS) as Array<[AdminLoginName, AdminLoginName]>;
	const follower = entries.find(([, watched]) => watched === subject)?.[0];
	return follower ? { deviceId: deviceIdFor(follower), subjectName: displayName(subject) } : null;
}

/** The current phone watched by this follower, if this exact phone owns that relationship. */
export function followedSubjectFor(followerDeviceId: string): { deviceId: string; subjectName: string } | null {
	const follower = deviceOwner(followerDeviceId);
	if (!follower || deviceIdFor(follower) !== followerDeviceId) {
		return null;
	}
	const subject = FOLLOWS[follower];
	return subject ? { deviceId: deviceIdFor(subject), subjectName: displayName(subject) } : null;
}

/** Whether anyone follows this device. The app's only record of who the rider is. */
export function isFollowed(deviceId: string): boolean {
	return followerFor(deviceId) != null;
}

/**
 * Devices that belong to someone but are not where an admin code is sent.
 *
 * Empty, and worth keeping empty: both Home Screen PWAs were retired on 2026-08-30 and their rows
 * deleted with them. This exists for the case of a second phone that is genuinely someone's — not
 * as a graveyard.
 */
const EXTRA_DEVICE_OWNERS: Record<string, AdminLoginName> = {};

/** Whose phone this is, whether or not it is the one an admin code goes to. */
export function deviceOwner(deviceId: string | null | undefined): AdminLoginName | null {
	const value = (deviceId ?? "").trim().toLowerCase();
	if (value.length < 3) {
		return null;
	}
	return adminLoginForDevice(value) ?? EXTRA_DEVICE_OWNERS[value.slice(0, 3)] ?? null;
}

export async function adminLoginDeviceLabel(value: string): Promise<string> {
	const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value.trim().toLowerCase()));
	const decoy = Array.from(new Uint8Array(digest).slice(0, 2), (byte) => byte.toString(16).padStart(2, "0")).join("").slice(0, 3);
	return resolveAdminLogin(value)?.deviceId.slice(0, 3) ?? decoy;
}
